import React, { type ReactNode, useCallback, useEffect, useState } from 'react';
import dayjs from 'dayjs';
import {
  ActionIcon,
  Alert,
  Anchor,
  Avatar,
  Badge,
  Box,
  Button,
  Grid,
  Group,
  Loader,
  Menu,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  Ban,
  Building2,
  CircleCheck,
  EllipsisVertical,
  Gauge,
  Hash,
  KeyRound,
  Mail,
  Map as MapIcon,
  MapPin,
  Pencil,
  Phone,
  ShieldCheck,
  Trash2,
  Truck,
  Warehouse,
} from 'lucide-react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Layout } from '../components/Layout.tsx';
import {
  ClientUsage,
  ConfirmModal,
  PageHeader,
  SeenAt,
  StatusBadge,
} from '../components/app/index.ts';
import {
  clientActivityAPI,
  employeeAPI,
  moduleAPI,
  regionAPI,
} from '../services/api.ts';
import { toast } from '../lib/toast.ts';

const isReport = (module: any) =>
  String(module.module_name || '').toLowerCase().includes('report');

// Odoo sends the text "false" for an empty field.
const text = (value: any) =>
  value && value !== 'false' ? String(value) : '';

/** A titled card of the profile, with an optional control on the right. */
const Card = ({
  title,
  action,
  children,
}: {
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) => (
  <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'lg' }}>
    <Group justify="space-between" mb="md" gap="sm">
      <Text fw={600}>{title}</Text>
      {action}
    </Group>
    {children}
  </Paper>
);

/** An icon with a small label and its value. */
const Field = ({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) => (
  <Group gap="sm" align="flex-start" wrap="nowrap">
    <ThemeIcon variant="light" color="gray" size="lg">
      {icon}
    </ThemeIcon>
    <Stack gap={0} miw={0}>
      <Text size="xs" c="dimmed">
        {label}
      </Text>
      <Text size="sm" fw={500} component="div" style={{ wordBreak: 'break-word' }}>
        {children || (
          <Text span inherit c="dimmed" fw={400}>
            Not set
          </Text>
        )}
      </Text>
    </Stack>
  </Group>
);

/** One headline number under the user's name. */
const Stat = ({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) => (
  <Paper withBorder shadow="sm" p="md">
    <Group gap="sm" wrap="nowrap">
      <ThemeIcon variant="light" size={40}>
        {icon}
      </ThemeIcon>
      <Stack gap={0} miw={0}>
        <Text size="xs" c="dimmed">
          {label}
        </Text>
        <Text fw={600} size="lg" component="div" lh={1.3}>
          {children}
        </Text>
      </Stack>
    </Group>
  </Paper>
);

/** Names as pills, or a line saying there are none. */
const Pills = ({
  items,
  icon,
  empty,
}: {
  items: any[];
  icon: ReactNode;
  empty: string;
}) =>
  items.length > 0 ? (
    <Group gap="xs">
      {items.map((item) => (
        <Badge
          key={item.id}
          variant="light"
          color="gray"
          size="lg"
          tt="none"
          fw={500}
          leftSection={icon}
        >
          {item.display_name || item.name}
        </Badge>
      ))}
    </Group>
  ) : (
    <Text size="sm" c="dimmed">
      {empty}
    </Text>
  );

/** One user's profile: `/employees/:id`. */
export const EmployeeProfile: React.FC = () => {
  const { id } = useParams();
  const employeeId = Number(id);
  const navigate = useNavigate();
  // The list this profile was opened from; the Users list otherwise.
  const from = useLocation().state?.from;
  const back = typeof from === 'string' ? from : '/employees';

  const [employee, setEmployee] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Loaded after the profile; the page still shows if one of them fails.
  const [region, setRegion] = useState('');
  const [permissions, setPermissions] = useState<any[] | null>(null);
  const [activity, setActivity] = useState<any | null | undefined>(undefined);
  const [confirm, setConfirm] = useState<'status' | 'delete' | null>(null);
  const [working, setWorking] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await employeeAPI.details(employeeId);
      setEmployee(response.data);
    } catch (err: any) {
      setError(
        err.response?.data?.message || err.message || 'Failed to load user'
      );
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    setEmployee(null);
    setPermissions(null);
    setActivity(undefined);
    setRegion('');
    load();
    moduleAPI
      .getEmployeePermissions(employeeId)
      .then((response) => setPermissions(response.data?.permissions || []))
      .catch(() => setPermissions([]));
  }, [employeeId, load]);

  const regionId = employee?.region_id;
  const lookup = employee?.work_email || employee?.name;
  useEffect(() => {
    if (!regionId) return;
    regionAPI
      .getById(regionId)
      .then((response) => setRegion(response.data?.region?.name || ''))
      .catch(() => undefined);
  }, [regionId]);

  // User Activity is searched by email; this user's row is picked by id.
  useEffect(() => {
    if (!lookup) return;
    clientActivityAPI
      .list({ search: lookup, limit: 50 })
      .then((response) =>
        setActivity(
          (response.data || []).find(
            (row: any) => row.employee_id === employeeId
          ) || null
        )
      )
      .catch(() => setActivity(null));
  }, [lookup, employeeId]);

  const active = employee?.status === 1;

  const toggleStatus = async () => {
    try {
      setWorking(true);
      const response = await employeeAPI.toggleStatus(employeeId);
      if (response.success) {
        toast.success(response.message);
        await load();
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setWorking(false);
      setConfirm(null);
    }
  };

  const remove = async () => {
    try {
      setWorking(true);
      const response = await employeeAPI.delete(employeeId);
      if (response.success) {
        toast.success(response.message);
        navigate('/employees');
      }
    } catch (err: any) {
      toast.error(err.message);
    } finally {
      setWorking(false);
      setConfirm(null);
    }
  };

  if (loading && !employee) {
    return (
      <Layout>
        <PageHeader back={back} title="User profile" />
        <Group justify="center" h={240}>
          <Loader />
        </Group>
      </Layout>
    );
  }

  if (!employee) {
    return (
      <Layout>
        <PageHeader back={back} title="User profile" />
        <Alert color="red" variant="light">
          <Group justify="space-between" gap="sm">
            <Text size="sm">{error || 'User not found'}</Text>
            <Button size="compact-sm" variant="subtle" onClick={load}>
              Try again
            </Button>
          </Group>
        </Alert>
      </Layout>
    );
  }

  const territories: any[] = employee.territories || [];
  const warehouses: any[] = employee.warehouses || [];
  const manager = employee.manager;
  const ownManager = manager && manager.id === employee.id;
  const role = employee.is_region_manager
    ? 'Region Manager'
    : employee.is_territory_manager
      ? 'Territory Manager'
      : '';
  const allowed = (permissions || []).filter(
    (module) => (module.granted_permissions || []).length > 0
  );

  const permissionGroup = (title: string, reports: boolean) => {
    const all = (permissions || []).filter(
      (module) => isReport(module) === reports
    );
    if (all.length === 0) return null;
    return (
      <Stack gap="xs">
        <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
          {title}
        </Text>
        <Group gap="xs">
          {all.map((module) => {
            const on = (module.granted_permissions || []).length > 0;
            return (
              <Badge
                key={module.module_id}
                variant={on ? 'light' : 'outline'}
                color={on ? 'green' : 'gray'}
                size="lg"
                tt="none"
                fw={500}
                leftSection={on ? <CircleCheck size={14} /> : <Ban size={14} />}
                opacity={on ? 1 : 0.6}
              >
                {module.module_name}
              </Badge>
            );
          })}
        </Group>
      </Stack>
    );
  };

  return (
    <Layout>
      <PageHeader back={back} title="User profile" />

      <Stack gap="lg">
        {/* Who this is, and what can be done with them. */}
        <Paper withBorder shadow="sm" p={{ base: 'md', sm: 'xl' }}>
          <Group justify="space-between" align="flex-start" gap="lg">
            <Group gap="lg" wrap="nowrap" align="center" miw={0}>
              <Avatar
                name={employee.name}
                color="initials"
                size={84}
                radius={84}
              />
              <Stack gap={6} miw={0}>
                <Group gap="sm">
                  <Title order={3}>{employee.name}</Title>
                  <StatusBadge status={active ? 'Active' : 'Inactive'} />
                  {role && (
                    <Badge variant="light" color="blue">
                      {role}
                    </Badge>
                  )}
                </Group>
                <Group gap="lg" c="dimmed">
                  {text(employee.work_email) && (
                    <Group gap={6} wrap="nowrap">
                      <Mail size={15} />
                      <Text size="sm">{employee.work_email}</Text>
                    </Group>
                  )}
                  {text(employee.mobile_phone) && (
                    <Group gap={6} wrap="nowrap">
                      <Phone size={15} />
                      <Text size="sm">{employee.mobile_phone}</Text>
                    </Group>
                  )}
                  <Group gap={6} wrap="nowrap">
                    <Hash size={15} />
                    <Text size="sm">ID {employee.id}</Text>
                  </Group>
                </Group>
              </Stack>
            </Group>

            <Group gap="sm">
              <Button
                component={Link}
                to={`/employees/${employee.id}/edit`}
                leftSection={<Pencil size={16} />}
              >
                Edit
              </Button>
              <Button
                component={Link}
                to={`/employees/${employee.id}/permissions`}
                variant="default"
                leftSection={<KeyRound size={16} />}
              >
                Permissions
              </Button>
              <Menu position="bottom-end" withinPortal>
                <Menu.Target>
                  <ActionIcon
                    variant="default"
                    size="lg"
                    aria-label="More actions"
                  >
                    <EllipsisVertical size={18} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    leftSection={
                      active ? <Ban size={16} /> : <CircleCheck size={16} />
                    }
                    onClick={() => setConfirm('status')}
                  >
                    {active ? 'Deactivate user' : 'Activate user'}
                  </Menu.Item>
                  <Menu.Item
                    color="red"
                    leftSection={<Trash2 size={16} />}
                    onClick={() => setConfirm('delete')}
                  >
                    Delete user
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>
        </Paper>

        <SimpleGrid cols={{ base: 1, xs: 2, lg: 4 }} spacing="lg">
          <Stat icon={<MapPin size={20} />} label="Territories">
            {territories.length}
          </Stat>
          <Stat icon={<Warehouse size={20} />} label="Warehouses">
            {warehouses.length}
          </Stat>
          <Stat icon={<ShieldCheck size={20} />} label="Allowed modules">
            {permissions === null ? (
              <Skeleton h={20} w={60} mt={4} />
            ) : (
              `${allowed.length} of ${permissions.length}`
            )}
          </Stat>
          <Stat icon={<CircleCheck size={20} />} label="Last seen">
            {activity === undefined ? (
              <Skeleton h={20} w={90} mt={4} />
            ) : (
              <SeenAt at={activity?.last_seen_at} size="lg" />
            )}
          </Stat>
        </SimpleGrid>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Stack gap="lg">
              <Card title="Details">
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <Field icon={<Mail size={18} />} label="Email">
                    {text(employee.work_email)}
                  </Field>
                  <Field icon={<Phone size={18} />} label="Phone">
                    {text(employee.mobile_phone)}
                  </Field>
                  <Field icon={<Building2 size={18} />} label="Company">
                    {employee.company?.name}
                  </Field>
                  <Field icon={<Building2 size={18} />} label="Project">
                    {employee.project?.name}
                  </Field>
                </SimpleGrid>
              </Card>

              <Card title="Area">
                <Stack gap="md">
                  <Field icon={<MapIcon size={18} />} label="Region">
                    {region}
                  </Field>
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Territories
                    </Text>
                    <Pills
                      items={territories}
                      icon={<MapPin size={14} />}
                      empty="No territories assigned"
                    />
                  </Stack>
                  <Stack gap="xs">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
                      Warehouses
                    </Text>
                    <Pills
                      items={warehouses}
                      icon={<Warehouse size={14} />}
                      empty="No warehouses assigned"
                    />
                  </Stack>
                </Stack>
              </Card>

              <Card
                title="Permissions"
                action={
                  <Button
                    component={Link}
                    to={`/employees/${employee.id}/permissions`}
                    size="compact-sm"
                    variant="light"
                  >
                    Manage
                  </Button>
                }
              >
                {permissions === null ? (
                  <Stack gap="xs">
                    <Skeleton h={28} />
                    <Skeleton h={28} w="70%" />
                  </Stack>
                ) : permissions.length === 0 ? (
                  <Text size="sm" c="dimmed">
                    Permissions could not be loaded.
                  </Text>
                ) : (
                  <Stack gap="md">
                    {permissionGroup('Modules', false)}
                    {permissionGroup('Reports', true)}
                  </Stack>
                )}
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="lg">
              <Card title="Reports to">
                {employee.is_region_manager || ownManager ? (
                  <Text size="sm" c="dimmed">
                    A region manager has no manager of their own.
                  </Text>
                ) : manager ? (
                  <Group gap="sm" wrap="nowrap">
                    <Avatar name={manager.name} color="initials" size={44} />
                    <Stack gap={0} miw={0}>
                      <Anchor
                        component={Link}
                        to={`/employees/${manager.id}`}
                        fw={500}
                        size="sm"
                      >
                        {manager.name}
                      </Anchor>
                      {text(manager.work_email) && (
                        <Text size="xs" c="dimmed" truncate>
                          {manager.work_email}
                        </Text>
                      )}
                    </Stack>
                  </Group>
                ) : (
                  <Text size="sm" c="dimmed">
                    No manager assigned
                  </Text>
                )}
              </Card>

              <Card title="Vehicle">
                <Stack gap="md">
                  <Field icon={<Truck size={18} />} label="Vehicle">
                    {employee.vehicle?.name}
                  </Field>
                  <Field icon={<Gauge size={18} />} label="Meter reading">
                    {employee.meter_reading != null &&
                    employee.meter_reading !== ''
                      ? String(employee.meter_reading)
                      : ''}
                  </Field>
                </Stack>
              </Card>

              <Card title="Activity">
                {activity === undefined ? (
                  <Stack gap="xs">
                    <Skeleton h={18} />
                    <Skeleton h={18} w="60%" />
                  </Stack>
                ) : !activity ? (
                  <Text size="sm" c="dimmed">
                    {active
                      ? 'Not seen in the app or the dashboard yet.'
                      : 'Activity is shown for active users only.'}
                  </Text>
                ) : (
                  <Stack gap="md">
                    <Box>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={6}>
                        Mobile app
                      </Text>
                      <ClientUsage client="app" usage={activity.app} />
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={6}>
                        Team dashboard
                      </Text>
                      <ClientUsage client="web" usage={activity.web} />
                    </Box>
                  </Stack>
                )}
              </Card>

              {(employee.createdAt || employee.updatedAt) && (
                <Text size="xs" c="dimmed" ta="right">
                  {employee.createdAt &&
                    `Added ${dayjs(employee.createdAt).format('DD/MM/YYYY')}`}
                  {employee.createdAt && employee.updatedAt && ' · '}
                  {employee.updatedAt &&
                    `Updated ${dayjs(employee.updatedAt).format('DD/MM/YYYY HH:mm')}`}
                </Text>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>

      <ConfirmModal
        opened={confirm === 'status'}
        onClose={() => setConfirm(null)}
        onConfirm={toggleStatus}
        title={`${active ? 'Deactivate' : 'Activate'} User`}
        confirmLabel={active ? 'Deactivate' : 'Activate'}
        color={active ? 'red' : 'green'}
        loading={working}
      >
        Are you sure you want to {active ? 'deactivate' : 'activate'}{' '}
        {employee.name}?
      </ConfirmModal>

      <ConfirmModal
        opened={confirm === 'delete'}
        onClose={() => setConfirm(null)}
        onConfirm={remove}
        title="Delete User"
        confirmLabel="Delete"
        color="red"
        loading={working}
      >
        Are you sure you want to delete {employee.name}? This action cannot be
        undone.
      </ConfirmModal>
    </Layout>
  );
};

export default EmployeeProfile;
